<template>
  <div
    v-if="isLangSelectorVisible"
    class="prepeers-lang-switcher"
    :class="{
      'prepeers-lang-switcher--desktop-navbar': props.displayLayout === 'desktopNavbar',
      'prepeers-lang-switcher--mobile-menu-footer': props.displayLayout === 'mobileMenuFooter',
    }">
    <span
      v-for="localeOption in availableLocaleOptions"
      :key="localeOption.code"
      role="button"
      tabindex="0"
      class="prepeers-lang-switcher__flag-button"
      :class="{
        'prepeers-lang-switcher__flag-button--active': locale === localeOption.code,
      }"
      :aria-label="$t(`nav.lang.${localeOption.code}`)"
      @click="switchToLocale(localeOption.code)"
      @keydown.enter.prevent="switchToLocale(localeOption.code)"
      @keydown.space.prevent="switchToLocale(localeOption.code)">
      <span
        class="fi"
        :class="localeFlagClass(localeOption.code)"
        aria-hidden="true" />
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'

import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import type {
  PrePeersAppLocaleCode,
  PrePeersLangSwitcherDisplayLayout,
  PrePeersLangSwitcherLocaleOption,
  PrePeersLangSwitcherProps,
} from '~/types/PrePeersLangSwitcher'

/**
 * Définit les props du composant PrePeersLangSwitcher.
 */
const props: PrePeersLangSwitcherProps = defineProps({
  displayLayout: {
    type: String as PropType<PrePeersLangSwitcherDisplayLayout>,
    required: true,
  },
})

const emit = defineEmits<{
  localeChanged: []
}>()

const { locale, locales, setLocale } = useI18n()
const runtimeConfig = useRuntimeConfig()
const { track } = useTracking()

/**
 * Affiche le sélecteur lorsque le switcher de langue est activé via l'environnement.
 * @returns `true` si `NUXT_ENABLED_LANG_SWITCHER=true`.
 */
const isLangSelectorVisible: ComputedRef<boolean> = computed(
  (): boolean => runtimeConfig.public.enabledLangSwitcher,
)

/**
 * Locales i18n disponibles pour le sélecteur.
 * @returns Liste des codes langue configurés dans Nuxt i18n.
 */
const availableLocaleOptions: ComputedRef<PrePeersLangSwitcherLocaleOption[]> = computed(
  (): PrePeersLangSwitcherLocaleOption[] => {
    return locales.value.flatMap(
      (entry: { code: string } | string): PrePeersLangSwitcherLocaleOption[] => {
        const localeCode: string = typeof entry === 'string' ? entry : entry.code
        if (localeCode === 'fr' || localeCode === 'en' || localeCode === 'es') {
          return [{ code: localeCode }]
        }
        return []
      },
    )
  },
)

/**
 * Classe CSS du drapeau pour une locale.
 * @param {PrePeersAppLocaleCode} localeCode - Code langue (`fr`, `en`, `es`).
 * @returns {string} Classe `fi-*` du pack drapeaux.
 */
function localeFlagClass(localeCode: PrePeersAppLocaleCode): string {
  return localeCode === 'en' ? 'fi-gb' : `fi-${localeCode}`
}

/**
 * Bascule la langue de l'application, track l'événement PostHog et notifie le parent.
 * @param {PrePeersAppLocaleCode} targetLocaleCode - Code langue cible.
 * @returns {Promise<void>}
 */
async function switchToLocale(targetLocaleCode: PrePeersAppLocaleCode): Promise<void> {
  const previousLocaleCode: PrePeersAppLocaleCode = locale.value as PrePeersAppLocaleCode
  if (previousLocaleCode === targetLocaleCode) return

  await setLocale(targetLocaleCode)

  track(TRACKING_EVENTS.LOCALE_SWITCHED, {
    previous_locale: previousLocaleCode,
    new_locale: targetLocaleCode,
    display_layout: props.displayLayout,
  })

  emit('localeChanged')
}
</script>

<style scoped>
.prepeers-lang-switcher {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex-shrink: 0;
  padding-inline: var(--space-2);
  border-left: 1px solid var(--border-subtle);
  border-right: 1px solid var(--border-subtle);
}

.prepeers-lang-switcher--desktop-navbar {
  margin-inline-start: 0;
}

.prepeers-lang-switcher--mobile-menu-footer {
  justify-content: center;
  padding-inline: 0;
  border-left: none;
  border-right: none;
}

.prepeers-lang-switcher__flag-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  opacity: 0.5;
  cursor: pointer;
  transition: opacity var(--duration-fast) var(--ease-out);
  font-size: 1rem;
  line-height: 1;
  user-select: none;
}

.prepeers-lang-switcher__flag-button--active {
  opacity: 1;
}

.prepeers-lang-switcher__flag-button:hover {
  opacity: 0.8;
}

.prepeers-lang-switcher__flag-button:focus-visible {
  outline: 2px solid var(--accent-blue);
  outline-offset: 1px;
}
</style>
